import { useQuery } from '@apollo/client';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import React, { FC, useEffect, useMemo, useState } from 'react';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import {
  ReceiptType,
  useButton,
  useCurrency,
  useI18n,
  useLogger,
  useSelector,
  useStaffCan,
} from '~Hooks';
import { useReceiptCopy } from '~Hooks/useReceiptCopy';
import {
  RefundedInfoBox,
  ButtonsWrapper,
  SkeletonItem,
} from '~Scenes/Handy/Scenes/History/Order/styles';
import { COLORS, Input, ModalContent } from '~Scenes/Handy/styles';
import CopyToTable from '~Scenes/History/Components/CopyToTable';
import { TablesAndFloorPlansQuery_venue_floorPlans_tables as TableType } from '~Scenes/Handy/__queries__';
import Refunds from '~Scenes/Refunds';
import { isRefundPossible } from '~Scenes/Refunds/utils';
import { BlackButton, BlueButton, Box, Button, RedButton } from '~Styles';
import { combineArticles } from '~Utils/combineArticles';
import { GET_ORDER } from './queries';
import Heading from '~ComponentsImproved/Typography/Heading';
import printIcon from '~Assets/icons/print.svg';
import mailIcon from '~Assets/icons/mail.svg';
import refundIcon from '~Assets/icons/replay.svg';
import { GetOrder, GetOrderVariables } from './__queries__';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import { DateTime } from 'luxon';
import { capitalize } from 'lodash';
import Loader from '~ComponentsImproved/Loader';
import { handyBreakpoint } from '~Styles/breakpoints';

export type HistoryItemType = {
  itemId: string;
  title: string;
  amount: number;
  message?: string;
  isRefunded: boolean;
  discount: number;
  variations: {
    id: string;
    name: string;
  }[];
};

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  max-width: 600px;
  margin-bottom: 20px; // this because we ignore padding bottom in the parent to enable scroll to bottom for the left section
  border: 1px solid var(--colorBlueGray700);
  padding: 16px;
  width: 100%;
  margin: 0 auto;

  button {
    word-break: break-word;
  }

  ${handyBreakpoint} {
    border: none;
    background: transparent;
  }
`;

const Header = styled(Box)`
  ${handyBreakpoint} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const OrderItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderItemWrapper = styled.div<{ isRefunded?: boolean }>`
  color: var(--colorWhite);
  letter-spacing: 0.24px;
  font-size: 14px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--colorBlueGray1000);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderItemName = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  padding-right: 8px;
`;

const VariationsList = styled.div`
  font-size: 12px;
  margin-top: 4px;
  color: var(--colorBlueGray500);
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
  opacity: 0.7;
  margin-right: 8px;
`;

const TotalRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const RefundedTag = styled.span`
  border: 1px solid var(--colorUIRed);
  border-radius: 2px;
  padding: 2px 4px;
  color: var(--colorUIRed);
  font-size: 12px;
  height: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const RefundRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--colorBlueGray1000);
`;

const RefundItemsList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Amount = styled.div`
  min-width: 60px;
  text-align: right;
`;

const DetailsSection = styled.div`
  margin-top: 24px;
`;

const PrintRefundButton: FC<{ onPrint: () => void }> = ({ onPrint }) => {
  const button = useButton({ onClick: onPrint });
  return (
    <BlackButton {...button} style={{ minWidth: 'auto', padding: '4px 8px' }}>
      <Icon src={printIcon} color="var(--colorWhite)" height="20px" width="20px" />
    </BlackButton>
  );
};

interface Props {
  orderId: string;
  onHandyCopyOrderComplete?: (table: TableType, chequeId: string) => void;
}

const HistoryOrder: FC<Props> = ({ orderId, onHandyCopyOrderComplete }) => {
  const logger = useLogger('history');
  const { i18n } = useI18n();
  const currency = useCurrency();
  const receiptCopy = useReceiptCopy();
  const { isShowing: refundsModalShowing, toggle: toggleRefundsModal } = useModal();
  const { isShowing: emailModalIsShowing, toggle: toggleEmailModal } = useModal();
  const [receiptEmail, setReceiptEmail] = useState<string | undefined>();
  const staffCan = useStaffCan();
  const [receiptCopyPrinted, setReceiptCopyPrinted] = useState(false);
  const country = useSelector((state) => state.country);

  // Reset receipt copy printed state when switching orders
  useEffect(() => {
    setReceiptCopyPrinted(false);
  }, [orderId]);

  const orderQuery = useQuery<GetOrder, GetOrderVariables>(GET_ORDER, {
    skip: !orderId,
    fetchPolicy: 'no-cache',
    variables: {
      id: orderId!,
    },
  });

  const order = orderQuery.data?.order;

  const refundPossible = useMemo(() => {
    // Wait for order to be loaded before checking refund possibility
    if (!order) return false;
    return isRefundPossible(order);
  }, [order]);

  useEffect(() => {
    if (order && order?.user?.email) {
      setReceiptEmail(order.user.email);
    }
  }, [order]);

  const formattedItems: HistoryItemType[] = useMemo(
    () =>
      order?.items.map((item) => ({
        itemId: item.entry.id,
        title: i18n(item.entry.item.titleLang),
        message: item.message,
        amount: item.price.amount - item.price.discount,
        isRefunded: item.isRefunded,
        discount: item.price.discount,
        variations: item.variations.filter((variation) => variation.price > 0),
      })) || [],
    [order, i18n],
  );

  const combinedItems = useMemo(() => combineArticles(formattedItems), [formattedItems]);

  const refundedAt = order?.statusHistory.find(
    (item: { status: ORDER_STATUS; timeStamp: number }) => item.status === ORDER_STATUS.REFUNDED,
  );

  const orderHasBeenRefunded = order?.status === ORDER_STATUS.REFUNDED;

  const isMultiPaymentWithAdyen =
    order?.payment?.subPayments &&
    order?.payment?.subPayments?.length > 1 &&
    order?.payment?.subPayments.some((payment) => payment.type === 'ADYEN');

  const printReceiptCopybutton = useButton({
    onClick: async () => {
      if (!order) {
        return;
      }
      logger.info('Printing receipt copy', { orderId: order?.id, easyId: order?.easyId });

      await receiptCopy.printReceiptCopy({
        order,
        type: ReceiptType.Copy,
        isCopy: true,
      });

      setReceiptCopyPrinted(true);
    },
    disabled: orderHasBeenRefunded || order?.hasPrintedReceiptCopy || receiptCopyPrinted,
    disabledToast: i18n('History.PrintReceiptCopy.OnlyOne'),
  });

  const emailReceiptCopybutton = useButton({
    onClick: () => {
      toggleEmailModal();
    },
  });

  const refundButton = useButton({
    onClick: () => {
      logger.info('Opening refund modal', {
        orderId: order?.id,
        easyId: order?.easyId,
        orderStatus: order?.status,
        refundPossible,
      });
      toggleRefundsModal();
    },
    disabled: !staffCan.REFUND || !refundPossible || orderQuery.loading || isMultiPaymentWithAdyen,
    disabledToast: isMultiPaymentWithAdyen
      ? i18n('Refunds.Disabled.MultiPayment')
      : order?.status === ORDER_STATUS.REFUNDED
      ? i18n('Refunds.Disabled.AlreadyRefunded')
      : order?.status !== ORDER_STATUS.COMPLETED
      ? i18n('Refunds.Disabled.OrderNotCompleted')
      : !staffCan.REFUND
      ? i18n('Refunds.Disabled.StaffNotAllowed')
      : undefined,
  });

  const printRefundReceiptCopybutton = useButton({
    onClick: () => {
      if (!order) {
        return;
      }
      logger.info('Printing refund receipt copy', { orderId: order?.id, easyId: order?.easyId });
      receiptCopy.printReceiptCopy({
        order,
        type: ReceiptType.Refund,
        isCopy: true,
      });
    },
  });

  const sendEmailButton = useButton({
    onClick: () => {
      if (!order || !receiptEmail) {
        return;
      }

      receiptCopy.emailReceiptCopy({
        order,
        email: receiptEmail,
      });

      logger.info('Sending receipt copy via email', {
        orderId: order?.id,
        easyId: order?.easyId,
        email: receiptEmail,
      });

      // Wait one second before closing the modal
      setTimeout(() => {
        toggleEmailModal();
      }, 1000);
    },
  });

  // Group refunds by refundReceiptNumber so items refunded together appear as one entry
  // Also include items that are marked as isRefunded but don't appear in order.refunds (legacy refunds)
  const groupedRefunds = useMemo(() => {
    const groups = new Map<
      number,
      { refundReceiptNumber: number; refundDate: string; itemIds: string[] }
    >();

    if (order?.refunds?.length) {
      for (const refund of order.refunds) {
        const existing = groups.get(refund.refundReceiptNumber);
        if (existing) {
          if (refund.itemId) {
            existing.itemIds.push(refund.itemId);
          }
        } else {
          groups.set(refund.refundReceiptNumber, {
            refundReceiptNumber: refund.refundReceiptNumber,
            refundDate: refund.refundDate,
            itemIds: refund.itemId ? [refund.itemId] : [],
          });
        }
      }
    }

    // Add legacy refunds for items/orders refunded before the refunds field existed
    if (order?.items) {
      const allRefundedItemIds = new Set(
        order.refunds?.map((r) => r.itemId).filter(Boolean) ?? [],
      );
      const hasLegacyFullRefund = order.status === ORDER_STATUS.REFUNDED && !order.refunds?.length;
      const legacyRefundedItems = order.items.filter(
        (item) => item.isRefunded && !allRefundedItemIds.has(item.id),
      );

      if (hasLegacyFullRefund || legacyRefundedItems.length > 0) {
        const refundDate = refundedAt?.timestamp
          ? DateTime.fromMillis(refundedAt.timestamp).toISO() ?? ''
          : typeof order.createdAt === 'number'
          ? DateTime.fromMillis(order.createdAt).toISO() ?? ''
          : order.createdAt ?? '';

        groups.set(0, {
          refundReceiptNumber: 0,
          refundDate,
          // Empty itemIds for full order refund, specific ids for partial
          itemIds: hasLegacyFullRefund ? [] : legacyRefundedItems.map((item) => item.id),
        });
      }
    }

    return Array.from(groups.values());
  }, [order?.refunds, order?.items, order?.createdAt, refundedAt]);

  // Function to print refund receipt for a specific item
  const printRefundReceipt = (itemIds: string[], refundReceiptNumber: number) => {
    if (!order) return;
    logger.info('Printing refund receipt copy', {
      orderId: order.id,
      itemIds,
      refundReceiptNumber,
    });
    receiptCopy.printReceiptCopy({
      order,
      type: ReceiptType.Refund,
      isCopy: true,
      itemIds: itemIds.length > 0 ? itemIds : undefined,
      refundReceiptNumber,
    });
  };

  const formatOrderType = (type: string) => {
    switch (type) {
      case 'EAT_HERE':
        return i18n('ServiceType.EatHere');
      case 'TAKE_AWAY':
        return i18n('ServiceType.TakeAway');
      case 'DELIVERY':
        return i18n('ServiceType.Delivery');
      default:
        return type.toLowerCase().replace('_', ' ');
    }
  };

  const formatPaymentType = (type: string) => {
    switch (type) {
      case 'CASH':
        return i18n('PaymentType.Cash');
      case 'ADYEN':
        return i18n('PaymentType.Card');
      case 'IZETTLE':
        return i18n('PaymentType.Card');
      case 'INVOICE':
        return i18n('PaymentType.Invoice');
      case 'PRINTED_INVOICE':
        return i18n('PaymentType.PrintedInvoice');
      case 'ADYEN_MANUELL':
        return i18n('PaymentType.AdyenManuell');
      case 'GIFT_CARD':
        return i18n('PaymentType.GiftCard');
      case 'MICRODEB':
        return i18n('PaymentType.GiftCard');
      case 'RETAIN24':
        return i18n('PaymentType.GiftCard');
      case 'BAEMINGO_GIFTCARD':
        return i18n('PaymentType.BaemingoGiftCard');
      default:
        return capitalize(type.toLowerCase().replace('_', ' '));
    }
  };

  if (orderQuery.loading) {
    return (
      <Wrapper>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{ height: '400px' }}>
          <Loader size="sm" color="#fff" />
        </Box>
      </Wrapper>
    );
  }

  if (!order) {
    logger.warn('Order not found', { orderId });
    return (
      <Box padding="medium">
        <Paragraph level={2} color="var(--colorWhite)">
          {i18n('Error.SomethingWrong')}
        </Paragraph>
      </Box>
    );
  }

  const paymentsList = order.payment.subPayments.length
    ? order.payment.subPayments
    : [order.payment];

  return (
    <Wrapper>
      <>
        <Header display="flex" alignItems="center" gap="xs" justifyContent="space-between">
          <Box display="flex" gap="small">
            <Heading level={2} color="var(--colorWhite)">
              {`${i18n('History.Order')} #${order?.easyId ?? ''}`}
            </Heading>
            <CopyToTable
              order={order}
              onHandyComplete={(table, chequeId) => {
                if (onHandyCopyOrderComplete) {
                  onHandyCopyOrderComplete(table, chequeId);
                }
              }}
            />
          </Box>
          <Paragraph level={4} color="var(--colorBlueGray500)">
            {order.origin + ', ' + formatOrderType(order.type)}
          </Paragraph>
        </Header>

        {orderHasBeenRefunded && refundedAt && (
          <RefundedInfoBox>
            {i18n('Refunds.Refunded')}
            {': '}
            {DateTime.fromMillis(refundedAt.timestamp).toFormat('yyyy-MM-dd HH:mm')}
          </RefundedInfoBox>
        )}

        <ButtonsWrapper>
          <BlackButton {...printReceiptCopybutton}>
            <Icon src={printIcon} color="var(--colorWhite)" height="24px" />
            {i18n('History.PrintReceiptCopy')}
          </BlackButton>

          <BlackButton {...emailReceiptCopybutton}>
            <Icon src={mailIcon} color="var(--colorWhite)" height="24px" />
            {i18n('History.EmailReceiptCopy')}
          </BlackButton>

          {order?.status === ORDER_STATUS.REFUNDED && (
            <BlackButton {...printRefundReceiptCopybutton}>
              <Icon src={printIcon} color="var(--colorWhite)" height="24px" />
              {i18n('History.PrintRefundReceiptCopy')}
            </BlackButton>
          )}

          {order?.status !== ORDER_STATUS.REFUNDED && (
            <RedButton {...refundButton}>
              <Icon src={refundIcon} color="var(--colorWhite)" height="24px" />
              {i18n('History.Refund')}
            </RedButton>
          )}
        </ButtonsWrapper>

        {/* Order items, list similar to the list in the Cart */}
        <Box marginTop="medium">
          <OrderItemsList>
            {orderQuery.loading &&
              Array.from({ length: 3 }).map((_, index) => <SkeletonItem key={index} />)}
            {combinedItems.map((item) => {
              const count = 'count' in item ? item.count : 1;
              const variations = item.variations?.map((v) => v.name) ?? [];
              const isRefunded = 'isRefunded' in item ? item.isRefunded : false;

              return (
                <OrderItemWrapper key={item.itemId} isRefunded={isRefunded}>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="row"
                    justifyContent="space-between">
                    <div>
                      <OrderItemName>
                        <span>{count}x</span> {item.title}
                      </OrderItemName>

                      {item.message && <em>{item.message.replace('[cn] ', '')}</em>}
                    </div>
                    <Box display="flex" alignItems="center" gap="small">
                      {(isRefunded || order.status === ORDER_STATUS.REFUNDED) && (
                        <RefundedTag>{i18n('Refunds.Refunded')}</RefundedTag>
                      )}
                      <Amount>
                        {item.discount && item.amount ? (
                          <StrikeThrough>
                            {currency.format(item.amount + item.discount)}
                          </StrikeThrough>
                        ) : null}
                        {item.amount ? currency.format(item.amount) : ''}
                      </Amount>
                    </Box>
                  </Box>
                  {variations.length > 0 && (
                    <VariationsList>{variations.join(', ')}</VariationsList>
                  )}
                </OrderItemWrapper>
              );
            })}
          </OrderItemsList>
          <Box marginTop="xs">
            {/* Total discount */}
            {order.items.reduce((sum, item) => sum + item.price.discount, 0) > 0 && (
              <TotalRow>
                <Paragraph level={4} color="var(--colorWhite)">
                  {i18n('History.Discount')}
                </Paragraph>
                <Paragraph level={4} color="var(--colorWhite)">
                  {currency.format(
                    order.items.reduce((sum, item) => sum + item.price.discount, 0),
                  )}
                </Paragraph>
              </TotalRow>
            )}

            {/* Total tip */}
            <TotalRow>
              <Paragraph level={4} color="var(--colorWhite)">
                {i18n('History.Tip')}
              </Paragraph>
              <Paragraph level={4} color="var(--colorWhite)">
                {currency.format(order.payment.tip || 0)}
              </Paragraph>
            </TotalRow>

            {/* Total price */}
            <TotalRow>
              <Heading level={4} color="var(--colorWhite)">
                {i18n('History.Total')}
              </Heading>
              <Heading level={4} color="var(--colorWhite)">
                {currency.format(order.payment.amount)}
              </Heading>
            </TotalRow>
          </Box>
        </Box>

        <DetailsSection>
          <Box marginBottom="medium">
            <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
              {i18n('History.Summary')}
            </Paragraph>
            {/* Total tip */}
            <TotalRow>
              <Paragraph level={4} color="var(--colorWhite)">
                {i18n('History.Total')}
              </Paragraph>
              <Paragraph level={4} color="var(--colorWhite)">
                {currency.format(order.payment.amount || 0)}
              </Paragraph>
            </TotalRow>

            <TotalRow>
              <Paragraph level={4} color="var(--colorWhite)">
                {i18n('History.TotalTip')}
              </Paragraph>
              <Paragraph level={4} color="var(--colorWhite)">
                {currency.format(order.payment.tip || 0)}
              </Paragraph>
            </TotalRow>

            {/* Total price */}
            <TotalRow>
              <Heading level={4} color="var(--colorWhite)">
                {i18n('History.TotalIncTip')}
              </Heading>
              <Heading level={4} color="var(--colorWhite)">
                {currency.format(order.payment.amount + (order.payment.tip || 0))}
              </Heading>
            </TotalRow>
          </Box>

          <Box marginBottom="medium">
            <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
              {i18n('History.PaymentDetails')}
            </Paragraph>
            {paymentsList.map((payment, index) => (
              <TotalRow key={index}>
                <Paragraph
                  level={4}
                  color="var(--colorWhite)"
                  style={{
                    width: '200px',
                  }}>
                  {formatPaymentType(payment.type)}
                </Paragraph>
                <Paragraph
                  level={4}
                  color="var(--colorWhite)"
                  style={{
                    textAlign: 'right',
                  }}>
                  <span>{currency.format(payment.amount + (payment.tip || 0))}</span>
                  {payment.tip ? (
                    <span>
                      {' ('}
                      {i18n('History.Incl')} {currency.format(payment.tip || 0)}{' '}
                      {i18n('History.tip')}
                      {')'}
                    </span>
                  ) : null}
                </Paragraph>
              </TotalRow>
            ))}
          </Box>

          {order.user.role === 'REGULAR' && (
            <Box marginBottom="medium">
              <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
                {i18n('History.UserDetails')}
              </Paragraph>
              <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                {order.user?.name || '-'}
              </Paragraph>
              {order.user?.email && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {order.user?.email || '-'}
                </Paragraph>
              )}
              {order.user?.phone && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {order.user?.phone}
                </Paragraph>
              )}
              {order.user?.address && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {order.user?.address}, {order.user?.zip || ''} {order.user?.city || ''}
                </Paragraph>
              )}
              {order.user?.orgNumber && (
                <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
                  {i18n('History.OrgNumber')}: {order.user?.orgNumber}
                </Paragraph>
              )}
            </Box>
          )}

          <Box marginBottom="medium">
            <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
              {i18n('History.OrderStatusHistory')}
            </Paragraph>
            {order.statusHistory.map((statusItem: any, index: number) => {
              return (
                <TotalRow key={index}>
                  <Paragraph level={4} color="var(--colorWhite)">
                    {statusItem.status}
                  </Paragraph>
                  <Paragraph level={4} color="var(--colorWhite)">
                    {statusItem.timestamp
                      ? DateTime.fromMillis(statusItem.timestamp).toFormat('yyyy-MM-dd HH:mm')
                      : i18n('History.UnknownTimestamp')}
                  </Paragraph>
                </TotalRow>
              );
            })}
          </Box>

          {order.payment?.metadata?.cardDetails &&
            order.payment?.metadata?.cardDetails.cardType !== null && (
              <Box marginBottom="medium">
                <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
                  {i18n('History.CardDetails')}
                </Paragraph>
                {Object.entries(order.payment.metadata.cardDetails).map(([key, value]) => {
                  if (key === '__typename') {
                    return null;
                  }

                  if (key === 'cardType') {
                    value = `${i18n('Receipt.CardType')}: ${value}`;
                  }

                  if (['tsi', 'tvr', 'aid'].includes(key) && value) {
                    value = `${key.toUpperCase()}: ${value}`;
                  }

                  return (
                    <Paragraph key={key} level={4} color="var(--colorWhite)" bottom={4}>
                      {value}
                    </Paragraph>
                  );
                })}
              </Box>
            )}

          {groupedRefunds.length > 0 && (
            <Box marginBottom="medium" marginTop="large">
              <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
                {i18n('History.Refunds')}
              </Paragraph>
              {groupedRefunds.map((group) => {
                // Full order refund if no itemIds
                const isFullOrderRefund = group.itemIds.length === 0;
                const refundedItemNames = group.itemIds
                  .map((itemId) => {
                    const item = order.items.find((i) => i.id === itemId);
                    return item ? i18n(item.entry.item.titleLang) : '';
                  })
                  .filter(Boolean);

                return (
                  <RefundRow key={group.refundReceiptNumber}>
                    <RefundItemsList>
                      <Paragraph level={4} color="var(--colorWhite)">
                        {isFullOrderRefund
                          ? i18n('History.FullOrderRefund')
                          : refundedItemNames.join(', ')}
                      </Paragraph>
                      <Paragraph level={4} color="var(--colorBlueGray500)">
                        {DateTime.fromISO(group.refundDate).toFormat('yyyy-MM-dd HH:mm')}
                      </Paragraph>
                    </RefundItemsList>
                    <PrintRefundButton
                      onPrint={() => printRefundReceipt(group.itemIds, group.refundReceiptNumber)}
                    />
                  </RefundRow>
                );
              })}
            </Box>
          )}
        </DetailsSection>

        {refundsModalShowing && (
          <Modal isShowing={refundsModalShowing} hide={toggleRefundsModal} noPadding>
            <Refunds
              orderId={order?.id ?? ''}
              selectedOrder={order}
              closeModal={toggleRefundsModal}
              onRefundCompleted={orderQuery.refetch}
            />
          </Modal>
        )}
        <Modal isShowing={emailModalIsShowing} hide={toggleEmailModal}>
          <ModalContent>
            <Heading level={3} color={COLORS.blueGray100}>
              {i18n('History.EmailReceiptCopy')}
            </Heading>
            <Input
              type="email"
              placeholder={i18n('History.Email.Placeholder')}
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.currentTarget.value)}
            />
            <BlueButton {...sendEmailButton} marginTop="small">
              {i18n('General.Send')}
            </BlueButton>
          </ModalContent>
        </Modal>
      </>
    </Wrapper>
  );
};

export default HistoryOrder;
